import { Chips, ChipsChangeParams } from "primereact/chips";

/**
 * CLE-2792 review round 6 — a keyword list that does not lose what you typed.
 *
 * 🚨 PENDING TEXT IS A VALUE, AND IT USED TO BE THROWN AWAY. PrimeReact's Chips only calls
 * `onChange` when a chip is committed, so wording typed and not followed by Enter existed only
 * inside the component: the selector it belonged to still counted as stating nothing, its sentence
 * did not mention it, and pressing Save discarded it — while the words sat on screen the whole
 * time, looking entered.
 *
 * So the pending text is LIFTED into the caller's state alongside the committed chips — the second
 * argument of `onChange` — and every reader of a keyword list asks for `withPending(...)` rather
 * than for the chips. The input itself keeps rendering what was typed, so this component takes the
 * text back OUT rather than back in; a `pending` prop would be a value nothing here could use. `addOnBlur` and
 * `separator=","` come from PrimeReact and cover clicking away and typing a comma; the lifted
 * `pending` covers everything else, including Save, which is the one that used to lose money-bearing
 * wording.
 */

/** The chips a field really holds: what was committed, plus whatever is still in the input. */
export const withPending = (
  values: string[] | undefined,
  pending: string | undefined
): string[] => {
  const typed = (pending ?? "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  const committed = values ?? [];
  return [...committed, ...typed.filter((part) => !committed.includes(part))];
};

interface IKeywordChipsProps {
  id?: string;
  testId: string;
  value: string[];
  placeholder?: string;
  onChange: (values: string[], pending: string) => void;
}

export function KeywordChips({
  id,
  testId,
  value,
  placeholder,
  onChange,
}: IKeywordChipsProps) {
  return (
    // The handlers sit on a wrapper rather than on Chips: PrimeReact forwards only `onKeyDown`, and
    // what is needed is the input's value AFTER the keystroke.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      onKeyUp={(event) =>
        onChange(value, (event.target as HTMLInputElement).value ?? "")
      }
    >
      <Chips
        id={id}
        data-testid={testId}
        className="w-full"
        value={value}
        placeholder={placeholder}
        addOnBlur
        separator=","
        // Chips clears its own input whenever it commits one, so the pending text goes with it.
        onChange={(e: ChipsChangeParams) => onChange(e.value ?? [], "")}
      />
    </div>
  );
}
